import {from} from "rxjs";
import type {MithraMaterializedApi} from "~/services/MithraMaterializedApi.ts";
import type AuthStore from "../../../authentication/AuthStore.tsx";
import type ProfileStore from "../../../stores/ProfileStore.tsx";
import type {BagStore} from "~/stores/BagStore.tsx";
import type {GoldenRecordStore} from "~/stores/GoldenRecordStore.ts";
import type {NestedRelatedDataModel} from "~/services/ApiHelpers.ts";
import type {single_bar_chart} from "~/components/visualization/single-barchart/SingleBarChartBuilder.tsx";
import type {MatReviewLevelStatisticsTreeSerializer} from "~/services/classes/MaterializedClasses.ts";
import {GRTableController} from "./GRTableController.tsx";
import {GRFilterController} from "./GRFilterController.ts";
import {BaseMuixApi} from "~/components/muix-data-grid/BaseMuixApi.ts";
import {BaseMuixPageController} from "~/components/muix-data-grid/controllers/BaseMuixPageController.ts";
import {computed, makeObservable, observable, reaction, runInAction} from "mobx";
import {convertSingleToChart} from "~/services/classes/CategorizationHelpers.ts";
import {selectMatReviewLevelStatistics} from "~/services/classes/MatReviewHelpers.ts";
import {AxoisRequestManager} from "~/stores/managers/RequestManager.tsx";

export class GRPageController extends BaseMuixPageController {
    readonly tableController: GRTableController;
    readonly filterController: GRFilterController;
    readonly reviewApi: BaseMuixApi;
    relatedDataFieldsSpec: NestedRelatedDataModel = {
        cluster: ['harmonized_name', 'type',
            {source: ['databag_id']}
        ],
        part: ['p_name', 'p_unit_spend', 'p_quantity', 'p_spend', 'p_local_spend',
            {supplier: ['s_name']},
            {business_unit: ['bu_name']},
        ],
    }
    readonly _reviewLevelStatistics = new AxoisRequestManager<{
        bagId: number,
        businessUnitId: undefined | null | number,
        taxonomySize: number
    }, [MatReviewLevelStatisticsTreeSerializer] | []>(
        ({
             bagId,
             businessUnitId,
             taxonomySize
         }) => from(this.api.listReviewLevelStatistics(bagId, businessUnitId, taxonomySize))
    );

    constructor(
        private grStore: GoldenRecordStore,
        private api: MithraMaterializedApi,
        auth: AuthStore,
        private profile: ProfileStore,
        protected bagStore: BagStore,
    ) {
        super(auth);
        makeObservable(this, {
            relatedDataFieldsSpec: observable,
            _reviewLevelStatistics: observable,
            reviewLevelStatistics: computed,
            _selectedStats: computed,
            parentCharts: computed,
        });
        this.tableController = new GRTableController(this, auth, profile, bagStore);
        this.filterController = new GRFilterController(this, api, profile, bagStore);
        this.reviewApi = new BaseMuixApi(this.api, '/golden-record-muix', this.tableController);
        reaction(() => ([this.bagStore.bagId]), ([bagId]) => {
            console.debug('GRPageController.reaction', bagId);
            runInAction(() => {
                this._reviewLevelStatistics.request({
                    bagId,
                    businessUnitId: undefined,
                    taxonomySize: 3
                });
            });
        });
        reaction(() => ([(this.bagStore.configStore.databagConfig?.columns ?? [])]), ([cc]) => {
            runInAction(() => {
                const partFields = this.relatedDataFieldsSpec.part.filter(item => typeof (item) === 'string');
                this.relatedDataFieldsSpec.part.push(
                    ...(cc.filter(c =>
                        c.mh_in_use && c.table_name === 'data_source_part' && !partFields.includes(c.column_name)
                    ).map(c => c.column_name))
                );
                const supplierFields = this.relatedDataFieldsSpec.part.find(
                    item => typeof (item) === 'object' && ('supplier' in item)
                )!['supplier'];
                this.relatedDataFieldsSpec.part.find(
                    item => typeof (item) === 'object' && ('supplier' in item)
                )!['supplier'].push(
                    ...(cc.filter(c =>
                        c.mh_in_use && c.table_name === 'data_source_supplier' && !supplierFields.includes(c.column_name)
                    ).map(c => c.column_name))
                );
                const buFields = this.relatedDataFieldsSpec.part.find(
                    item => typeof (item) === 'object' && ('business_unit' in item)
                )!['business_unit'];
                this.relatedDataFieldsSpec.part.find(
                    item => typeof (item) === 'object' && ('business_unit' in item)
                )!['business_unit'].push(
                    ...(cc.filter(c =>
                        c.mh_in_use && c.table_name === 'data_source_businessunit' && !buFields.includes(c.column_name)
                    ).map(c => c.column_name))
                );
            })
        });
    }

    get reviewLevelStatistics(): MatReviewLevelStatisticsTreeSerializer | undefined {
        return this._reviewLevelStatistics.result?.at(0);
    }

    get _selectedStats() {
        const s = this.reviewLevelStatistics;
        if (!s) {
            return undefined
        }
        return selectMatReviewLevelStatistics(s, this.filterController.selectedCategory)
    }

    get parentCharts(): {
        charts: {
            category: string,
            data: single_bar_chart.Data
        }[],
        max: number,
    } | undefined {
        if (!this._selectedStats) return undefined;
        const parentStats = this._selectedStats.slice(1);
        if (parentStats.length === 0) return undefined;
        const charts = parentStats.map((stat, i) => {
            const aiChangeChart = convertSingleToChart(stat, this.profile.currencySymbol, true);
            const data: single_bar_chart.Data = {
                mainLabel: `L${i + 1}: ${aiChangeChart.label}`,
                values: aiChangeChart.values,
            }
            return {category: aiChangeChart.category, data};
        })
        const COL_WIDTH = 6 / 9 // Show as width col=6, while the whole viz goes to col=9
        const max = Math.max(...charts.map(c => Math.max(...c.data.values.map(v => v.value)))) / COL_WIDTH;
        return {charts, max}
    }
}